'use client';

import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import {
  GalleryImage,
  ResponsiveImageDataWithBadges,
} from '@/app/[abTest]/[locale]/(spreadshirt)/shop/design/components/GalleryImage';
import { pdpParamsWithSyncAtom } from '@/atoms/pdpParamsAtom';
import { useImageBaseUrl } from '@/atoms/processEnvAtom';
import { useResolvedAppearances } from '@/components/ProductCard/AppearanceConfigProvider';
import { SprdResponsiveImageData } from '@/components/ResponsiveImage/SprdResponsiveImage';
import { getCompositionImageAltText, getProductTypeImageAltText } from '@/utility/seo';

type SizeSectionImages = {
  modelImagesWithDetail: Record<string, ModelImageWithDetails[]>;
  productType: ClientProductType;
  sellable?: Sellable;
  compositionName?: string;
  tag: 'cyo' | 'mp';
};

export const SizeSectionImages = ({
  modelImagesWithDetail,
  productType,
  sellable,
  compositionName,
  tag,
}: SizeSectionImages) => {
  const t = useTranslations('size.guide');
  const imageBaseUrl = useImageBaseUrl();
  const defaultAppearanceId = productType.defaultValues.defaultAppearance.id;
  const pdpParams = useAtomValue(pdpParamsWithSyncAtom);
  const appearanceId = pdpParams?.appearanceId ?? defaultAppearanceId;

  const currentModelImages = modelImagesWithDetail[appearanceId] ?? [];

  const hasModelImages = currentModelImages?.some((m) => m.model?.persons?.some((p) => p?.detail));
  const [appearance] = useResolvedAppearances([appearanceId]);

  if (!currentModelImages || !hasModelImages) return null;
  return (
    <div>
      <span className="font-medium">{t('modelHeadline')}</span>
      <ul className="mt-4 grid grid-cols-2 gap-x-2 gap-y-4 sm:grid-cols-3 md:grid-cols-2">
        {currentModelImages.map((modelImage) => {
          const person = modelImage?.model?.persons?.[0];

          if (!person || !person.detail) return null;

          const { modelPersonProductTypeSize, detail } = person;
          const nameAndSize = `${detail.name} (${detail.size.value} ${detail.size.unit})`;

          const sizeName = productType.sizes.find(
            (size) => +size.id === modelPersonProductTypeSize
          );

          return (
            <li key={modelImage.modelId} className="flex grow flex-col">
              <GalleryImage
                className="flex aspect-square grow object-top"
                imageBaseUrl={imageBaseUrl}
                image={
                  tag === 'mp' && sellable
                    ? ({
                        type: 'product',
                        vpString: sellable.vpString,
                        viewId: modelImage.viewId,
                        modelId: modelImage.model?.modelId,
                        appearanceId: modelImage.appearanceId,
                        crop: modelImage.crop,
                        loading: 'lazy',
                        priority: 'low',
                        alt: getCompositionImageAltText(
                          compositionName ?? '',
                          productType.name,
                          appearance?.name ?? '',
                          undefined,
                          modelImage.model?.persons
                        ),
                      } satisfies SprdResponsiveImageData)
                    : ({
                        type: 'productType',
                        productTypeId: productType.id,
                        viewId: modelImage.viewId,
                        modelId: modelImage.model?.modelId,
                        appearanceId: modelImage.appearanceId,
                        crop: modelImage.crop,
                        loading: 'lazy',
                        priority: 'low',
                        alt: getProductTypeImageAltText(
                          productType.name,
                          appearance?.name ?? '',
                          productType.views.find((v) => v.id === modelImage.viewId)?.name,
                          modelImage.model?.persons
                        ),
                      } satisfies ResponsiveImageDataWithBadges)
                }
              />
              <div className="mt-1 text-sm md:text-base">
                <span className="font-medium">{nameAndSize}</span>
                {sizeName && (
                  <span className="block">
                    {t('size')}: {sizeName.name}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
